import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";
import {
  Dialog,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { HttpClientContext, X_REQUEST_ID } from "../app/client";
import Loading from "./common/Loading";
import Error from "./common/Error";
import TaskLoader from "./common/tasks/TaskLoader";
import TaskView from "./common/tasks/TaskView";
import { Close } from "@mui/icons-material";

const categories = ["active", "approval", "historical"] as const;
type Category = (typeof categories)[number];

function Requests(props: { userId: number }) {
  const { userId } = props;

  const [searchParams, setSearchParams] = useSearchParams();
  const taskId = searchParams.get("id");
  const kind = searchParams.get("kind");

  const [category, setCategory] = useState(
    kind !== null && categories.includes(kind as Category) ? kind : "active"
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [requests, setRequests] = useState<string[]>([]);

  const [open, setOpen] = useState(false);

  const client = useContext(HttpClientContext);

  const loadRequests = useCallback(() => {
    if (taskId !== null) return;

    setLoading(true);
    setError(undefined);
    setRequests([]);

    client
      ?.get<string[]>(`/api/tasks/requests/${userId}/${category}`)
      .then((res) => setRequests(res.data))
      .catch((e: AxiosError) => setError(e.response?.headers[X_REQUEST_ID]))
      .finally(() => setLoading(false));
  }, [category, client, taskId, userId]);

  useEffect(loadRequests, [loadRequests]);

  useEffect(() => {
    if (taskId !== null) setOpen(true);
  }, [taskId]);

  const handleCategoryChange = (
    _event: React.MouseEvent<HTMLElement>,
    newCategory: Category | null
  ) => {
    if (newCategory !== null) {
      setRequests([]);
      setCategory(newCategory);
      setSearchParams({ kind: newCategory });
    }
  };

  let comp = <></>;
  if (loading) comp = <Loading />;
  if (error) comp = <Error error={error} />;
  if (requests.length === 0)
    comp = (
      <Typography variant="h6">
        There are no {category} requests linked to this account
      </Typography>
    );
  else
    comp = (
      <>
        {requests.map((r, i) => (
          <TaskLoader
            key={i}
            id={r}
            render={(task, reload) => (
              <TaskView
                task={task}
                callback={reload}
                expanded={r === taskId}
                archived={category === "historical"}
              />
            )}
          />
        ))}
      </>
    );

  const navigate = useNavigate();

  return (
    <>
      <ToggleButtonGroup
        value={category}
        fullWidth
        exclusive
        onChange={handleCategoryChange}
        sx={{ maxWidth: "sm" }}
      >
        <ToggleButton value="active">Active</ToggleButton>
        <ToggleButton value="approval">Approval</ToggleButton>
        <ToggleButton value="historical">Historical</ToggleButton>
      </ToggleButtonGroup>

      {comp}

      {taskId !== null && (
        <Dialog open={open}>
          <TaskLoader
            id={taskId}
            render={(task, reload) => (
              <TaskView
                task={task}
                callback={reload}
                expanded
                close={
                  <IconButton
                    onClick={() => navigate(`/requests?kind=${category}`)}
                  >
                    <Close />
                  </IconButton>
                }
              />
            )}
            close={() => navigate(`/requests?kind=${category}`)}
          />
        </Dialog>
      )}
    </>
  );
}

export default Requests;
